import { isSameObject } from 'app/utils/helpers';
import { ApiBase, LocationParamsV2 } from 'app/utils/types/apiTypes';
import { useEffect, useRef, useState } from 'react';

import useObserveLocation from './useObserveLocation';

const API_STATUS = {
  IDLE: 'idle',
  SUCCESS: 'success',
  ERROR: 'error',
  FAILED: 'failed',
  LOADING: 'loading',
};

type ApiOptions = {
  enabled: boolean;
  refetchInterval?: number | false;
};

type UseApiResponse<K> = {
  data: K | undefined;
  isIdle: boolean;
  isLoading: boolean;
  isError: boolean;
  isFailed: boolean;
  isSuccess: boolean;
  refetch: () => void;
};

const DEFAULT_API_OPTIONS = { enabled: true };

const useApi = <T extends ApiBase<unknown>, K>(
  asyncFn: (params: T) => Promise<K>,
  { allowedKeys, ...rest }: T['baseParams'] & LocationParamsV2,
  { enabled, refetchInterval = false }: ApiOptions = DEFAULT_API_OPTIONS
): UseApiResponse<K> => {
  const [apiStatus, setApiStatus] = useState(
    enabled ? API_STATUS.LOADING : API_STATUS.IDLE
  );

  const locationObserver = useObserveLocation(allowedKeys);

  const apiParamsRef = useRef<null | T['baseParams']>(null);
  const locationObserverRef = useRef<null | typeof locationObserver>(null);
  const dataRef = useRef<K | undefined>(undefined);
  const controllerRef = useRef<AbortController | null>(null);
  const interval = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = async (controller: AbortController) => {
    setApiStatus(API_STATUS.LOADING);

    try {
      const payload = {
        signal: controller.signal,
        baseParams: { ...(apiParamsRef.current ?? {}) },
        locationParams: { ...locationObserverRef.current },
      } as T;

      const data = await asyncFn(payload);

      dataRef.current = data;
      setApiStatus(API_STATUS.SUCCESS);
    } catch (error: any) {
      if (error?.name !== 'CanceledError') {
        setApiStatus(API_STATUS.ERROR);
      }
    }
  };

  const abort = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  };

  const fetchApiControllor = () => {
    if (!controllerRef.current) {
      controllerRef.current = new AbortController();
      fetchData(controllerRef.current);
    }
  };

  const refetchApi = () => {
    if (enabled) {
      abort();
      fetchApiControllor();
    }
  };

  useEffect(() => {
    return abort;
  }, []);

  useEffect(() => {
    if (refetchInterval && enabled) {
      interval.current = setInterval(refetchApi, refetchInterval);
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current as unknown as number);
      }
    };
  }, [refetchInterval, enabled]);

  useEffect(() => {
    if (enabled) {
      if (
        !isSameObject(apiParamsRef.current, rest) ||
        !isSameObject(locationObserver, locationObserverRef.current)
      ) {
        apiParamsRef.current = structuredClone(rest);
        locationObserverRef.current = locationObserver;

        abort();
        fetchApiControllor();
      }
    } else if (controllerRef.current) {
      // Data should not be cleared if just enabled is getting false.
      // Case: API is getting call with refetchInterval and in between enable is getting false
      // idally previous data should be present in that case.
      if (!refetchInterval) {
        dataRef.current = undefined;
      }

      apiParamsRef.current = null;
      locationObserverRef.current = null;

      abort();
      setApiStatus(API_STATUS.IDLE);
    }
  }, [locationObserver, enabled, rest]);

  return {
    data: dataRef.current,
    isIdle: apiStatus === API_STATUS.IDLE,
    isLoading: apiStatus === API_STATUS.LOADING,
    isError: apiStatus === API_STATUS.ERROR,
    isFailed: apiStatus === API_STATUS.FAILED,
    isSuccess: apiStatus === API_STATUS.SUCCESS,
    refetch: refetchApi,
  };
};

export default useApi;
